import React from 'react';
import Button from 'src/app/atoms/Button/Button';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import { CalculatorInputProps } from 'src/app/molecules/Calculator/CalculatorInput';
import ContentWithCalculator from 'src/app/molecules/ContentWithCalculator/ContentWithCalculator';
import useCalculatorController from 'src/hooks/useCalculatorController';
import useOnMobile from 'src/hooks/useOnMobile';

const ProfitCalculator = (): JSX.Element => {
  const {
    memberNumber,
    sliderIndexValue,
    setSliderIndexValue,
    subscriptionValue,
    setSubscriptionValue,
    courseValue,
    setCourseValue,
    annualRevenue,
    profitValue,
  } = useCalculatorController({
    eventLabel: 'ROI Calculator Interaction Standard',
    initialSliderIndexValue: 1,
  });

  const inputs: CalculatorInputProps[] = [
    {
      label: 'How many members?',
      onChange: (value: number | string) => setSliderIndexValue(Number(value)),
      type: 'text',
      value: memberNumber,
      rightPlaceholder: 'Members',
      slider: {
        min: 0,
        max: 9,
        step: 1,
        value: sliderIndexValue,
      },
    },
    {
      label: 'Subscription',
      onChange: (value: number | string) => setSubscriptionValue(Number(value)),
      value: subscriptionValue,
      rightPlaceholder: 'Per Month',
      type: 'number',
      max: 10000,
      min: 0,
      money: true,
      hint: '$48/month is the average price of a paid membership on Mighty.',
    },
    {
      label: 'Course Or Event',
      onChange: (value: number | string) => setCourseValue(Number(value)),
      value: courseValue,
      rightPlaceholder: 'One-time Fee',
      type: 'number',
      max: 10000,
      min: 0,
      money: true,
      hint: '$500 is the average price for a one-time fee, like for a course or event, on Mighty.',
    },
  ];

  const result = {
    lowerLabel: annualRevenue
      ? 'Annual Revenue'
      : "Our calculator maxes out just under $1B—but that's a limit on the tool, not your potential.",
    value: annualRevenue ? `$${profitValue}` : undefined,
  };

  const isMobile = useOnMobile(1280);

  return (
    <Grid>
      <Stack
        borderRadius={!isMobile ? 'radius-2xl' : 'radius-none'}
        background='bg-tertiary'
        overflow='hidden'
        padding='spacing-xl'
        justify='center'
        align='center'
      >
        <ContentWithCalculator
          heading={{
            type: 'heading',
            text: '$1M? Really?',
            size: 'xl',
          }}
          supportingText={{
            type: 'paragraph',
            text: 'It’s closer than you think. The average membership on Mighty charges $48/month. That means a big business can start with a small email list, existing membership—or even the contacts in your phone.',
            size: 'xl',
            color: 'text-tertiary',
          }}
          subHeading={{
            type: 'paragraph',
            size: 'lg',
            color: 'text-brand-secondary',
            text: 'Do the math on Mighty',
            fontWeight: 'font-weight-semibold',
          }}
          actions={
            <Stack marginTop='spacing-4xl'>
              <Button
                href='https://cohost.mn.co/?plan=free-trial'
                variant='secondary'
                size='xl'
              >
                Start a Free Trial
              </Button>
            </Stack>
          }
          calculator={{
            inputs,
            result,
          }}
        />
      </Stack>
    </Grid>
  );
};

export default ProfitCalculator;
